import React, { useCallback, useEffect, useState } from "react";

import { Dialog, Grid } from "@material-ui/core";
import { Field, Formik, useFormikContext, getIn } from "formik";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  getActiveLivestockAgentDeployment,
  getBusinesses,
  getContextByModalType,
  getSaleSubTypeById,
} from "selectors";
import { ModalTypes } from "constants/navigation";

import { SmallFaIcon } from "components/FaIcon";
import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/Input";
import { withNamespace } from "components/Form/FormikControls";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useFieldValue, useToggle } from "hooks";
import { SaleAction } from "actions";
import { SaleType } from "constants/sale";
import { PricingTypeId } from "constants/pricingTypes";

import { Form } from "../Form/Form";
import { Actions } from "../Form/Actions";
import { ModalTitle } from "../Form/ModalTitle";
import { createSaleValidationSchema } from "../Form/validation";
import {
  MobileTransactionalCreateSaleFormValues,
  defaultCreateSaleValues,
} from "../lib/types";
import {
  consignmentNamespace,
  saleLotNamespace,
  saleNamespace,
  skinsLotsNamespace,
  TransactionalSaleFormTabId,
  TransactionalSaleFormTabs,
} from "../constants";
import { MobileTransactionalCreateSaleModalTabOrder } from "../constants/sale";
import { WhenTab } from "../Tabs/WhenTab";
import { ReviewTab } from "../Tabs/ReviewTab";
import { WhoTab } from "./tabs/WhoTab";
import { WhatTab } from "./tabs/WhatTab";

const SaleTitleWrapper = styled.h4`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

interface SaleTitleHeaderProps {
  onClose: () => void;
}

const SaleTitleHeader = ({ onClose }: SaleTitleHeaderProps) => {
  const [isEditingSaleTitle, toggleIsEditingSaleTitle] = useToggle(false);
  const saleTitle = useFieldValue(withNamespace(saleNamespace, "sale_title"));

  return (
    <ModalTitle onClose={onClose} style={{ marginBottom: "-15px" }}>
      {isEditingSaleTitle ? (
        <Field name={withNamespace(saleNamespace, "sale_title")}>
          {({ field }) => (
            <Input
              {...field}
              autoFocus
              required
              onBlur={e => {
                field.onBlur(e);
                toggleIsEditingSaleTitle();
              }}
            />
          )}
        </Field>
      ) : (
        <>
          <Grid item xs={11}>
            <SaleTitleWrapper>{saleTitle}</SaleTitleWrapper>
          </Grid>
          <Grid item xs={1}>
            <SmallFaIcon
              icon={faPencil}
              color="black"
              onClick={toggleIsEditingSaleTitle}
            />
          </Grid>
        </>
      )}
    </ModalTitle>
  );
};

const buildSaleTitle = (vendorName, buyerName, saleType, saleSubType) => {
  const parties =
    vendorName && buyerName
      ? `${vendorName} - ${buyerName}`
      : vendorName || buyerName;
  const saleSubTypeName =
    !!saleSubType?.name && saleSubType?.name !== saleType
      ? saleSubType?.name
      : null;
  return [parties, saleType, saleSubTypeName].filter(Boolean).join(" ");
};

interface CreateSaleModalBodyProps {
  deploymentId: number | undefined;
  onClose: () => void;
}

const CreateSaleModalBody = ({
  deploymentId,
  onClose,
}: CreateSaleModalBodyProps) => {
  const context = useSelector(
    getContextByModalType(ModalTypes.MobileTransactionalCreateSaleModal),
  );
  const isContextSubmitting = context?.isContextSubmitting ?? false;
  const { values, setFieldValue, touched } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const {
    saleLot: { buyer_id: buyerId },
    consignment: { vendor_id: vendorId },
    sale: {
      sale_type: saleType,
      species_id: speciesId,
      sale_sub_type_id: saleSubTypeId,
    },
  } = values;

  const businessByIdLookup = useSelector(getBusinesses);
  const currentSaleSubType = useSelector(getSaleSubTypeById(saleSubTypeId));

  const buyerName = buyerId ? businessByIdLookup[buyerId]?.name || null : null;
  const vendorName = vendorId
    ? businessByIdLookup[vendorId]?.name || null
    : null;

  const saleTitleTouched = !!getIn(
    touched,
    withNamespace(saleNamespace, "sale_title"),
  );

  useEffect(() => {
    if (saleTitleTouched) {
      return;
    }
    const baseSaleTitle = buildSaleTitle(
      vendorName,
      buyerName,
      saleType,
      currentSaleSubType,
    );
    setFieldValue(withNamespace(saleNamespace, "sale_title"), baseSaleTitle);
  }, [
    vendorName,
    buyerName,
    saleType,
    currentSaleSubType,
    saleTitleTouched,
    setFieldValue,
  ]);

  const [activeTab, setActiveTab] = useState<TransactionalSaleFormTabId>(
    TransactionalSaleFormTabs.WHEN,
  );

  const renderTab = (tab: TransactionalSaleFormTabId): React.ReactNode => {
    switch (tab) {
      case TransactionalSaleFormTabs.WHO:
        return (
          <WhoTab
            saleLotNamespace={saleLotNamespace}
            consignmentNamespace={consignmentNamespace}
          />
        );
      case TransactionalSaleFormTabs.WHAT:
        return (
          <WhatTab
            deploymentId={deploymentId}
            consignmentNamespace={consignmentNamespace}
            skinsLotsNamespace={skinsLotsNamespace}
            speciesId={speciesId}
            saleType={saleType}
          />
        );
      case TransactionalSaleFormTabs.WHEN:
        return (
          <WhenTab
            consignmentNamespace={consignmentNamespace}
            saleNamespace={saleNamespace}
          />
        );
      case TransactionalSaleFormTabs.REVIEW:
        return (
          <ReviewTab
            deploymentId={deploymentId}
            speciesId={speciesId}
            saleNamespace={saleNamespace}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SaleTitleHeader onClose={onClose} />
      <Form
        activeTab={activeTab}
        changeTab={setActiveTab}
        tabOrder={MobileTransactionalCreateSaleModalTabOrder}
        saleLotNamespace={saleLotNamespace}
        saleNamespace={saleNamespace}
        consignmentNamespace={consignmentNamespace}
        renderTab={renderTab}
      />
      <Actions
        onClose={onClose}
        activeTab={activeTab}
        changeTab={setActiveTab}
        tabOrder={MobileTransactionalCreateSaleModalTabOrder}
        isContextSubmitting={isContextSubmitting}
      />
    </>
  );
};

interface MobileTransactionalCreateSaleModalProps {
  onClose: () => void;
}

export const MobileTransactionalCreateSaleModal = ({
  onClose,
}: MobileTransactionalCreateSaleModalProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const context = useSelector(
    getContextByModalType(ModalTypes.MobileTransactionalCreateSaleModal),
  );

  const dispatch = useDispatch();

  const { saleType, initialValues: initialValuesProp } = context;

  const deployment = useSelector(getActiveLivestockAgentDeployment);

  const {
    species_id: initialValuesSpeciesId,
    sale_sub_type_id: initialValuesSaleSubTypeId,
  } = initialValuesProp;

  const handleClose = () => setIsConfirmOpen(true);

  const handleSubmit = useCallback(
    (values: MobileTransactionalCreateSaleFormValues) => {
      dispatch(SaleAction.createMobileTransactionalSale(values));
    },
    [dispatch],
  );

  if (!deployment) {
    return null;
  }

  const defaultPricingTypeId =
    saleType === SaleType.PADDOCK
      ? PricingTypeId.PER_HEAD
      : PricingTypeId.PER_KILO;

  const mergedInitialValues: MobileTransactionalCreateSaleFormValues = {
    sale: {
      ...defaultCreateSaleValues.sale,
      date: new Date().toISOString().split("T")[0],
      sale_type: saleType,
      sale_sub_type_id: initialValuesSaleSubTypeId || null,
      species_id: initialValuesSpeciesId || null,
      deployment_ids: [deployment.id],
      deployment_sales: [
        {
          deployment_id: deployment.id,
        },
      ],
    },
    consignment: defaultCreateSaleValues.consignment,
    saleLot: defaultCreateSaleValues.saleLot,
    saleLots: defaultCreateSaleValues.saleLots?.map(lot => ({
      ...lot,
      pricing_type_id: defaultPricingTypeId,
    })),
    skinsLots: defaultCreateSaleValues.skinsLots,
  };

  return (
    <Dialog fullWidth fullScreen onClose={handleClose} open>
      <Formik
        initialValues={mergedInitialValues}
        validationSchema={createSaleValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        <>
          <CreateSaleModalBody
            onClose={handleClose}
            deploymentId={deployment.id}
          />
          <ConfirmDialog
            isOpen={isConfirmOpen}
            title="Are you sure you want to exit?"
            message="You will lose your unsaved changes."
            buttonMessage="Confirm"
            onCancel={() => setIsConfirmOpen(false)}
            onDelete={onClose}
          />
        </>
      </Formik>
    </Dialog>
  );
};
